import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { tPortalLib } from '../../locales';
import { type PortalTheme } from '../../theme';
import { NewsCardImageDefault } from './news-card-image-default.component';

export interface NewsCardProps {
  title: string;
  description: string;
  publishDate: string;
  portalId: string;
  imageUrl?: string;
  imageAltText?: string;
  onClick: () => void;
  newIndicatorEnabled?: boolean;
  getLastReadDate?: (portalId: string) => Promise<string | undefined>;
  ariaLabel?: string;
}

export const NewsCard = (props: NewsCardProps) => {
  const theme = useTheme<PortalTheme>();
  const a11y = tPortalLib.news.a11y;
  const [isNew, setIsNew] = useState<boolean>(false);

  useEffect(() => {
    if (!props.newIndicatorEnabled || !props.getLastReadDate) {
      setIsNew(false);
      return;
    }

    props.getLastReadDate(props.portalId).then((lastReadDate) => {
      if (!lastReadDate) {
        setIsNew(false);
        return;
      }

      const publishDate = new Date(props.publishDate).getTime();
      const lastRead = new Date(lastReadDate).getTime();

      setIsNew(publishDate >= lastRead);
    });
  }, [props.publishDate, props.newIndicatorEnabled, props.portalId]);

  return (
    <ThemeProvider theme={theme}>
      <Card
        title={props.title}
        sx={{
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.shape.borderRadius}px`,
          boxShadow: theme.boxShadow,

          '&:focus-within': {
            /*
             * The card is not a focusable component but is still clickable.
             * At the moment it doesn't need to be since there is a button
             * that perform the same action as clicking the card. But when
             * focusing a button in the card it should be more clear that we
             * have focused something in the card.
             *
             * An alternative solution would be to use a standard html button
             * instead and style it same as current card. But that would
             * circumvent theming which is needed in the future.
             */
            background: theme.palette.primary.dark,
          },
        }}
        aria-label={props.ariaLabel}
      >
        <CardActionArea
          onClick={() => {
            props.onClick();
          }}
          aria-label={a11y.readArticle({ title: props.title })}
          data-eventid="news-article"
        >
          {props.imageUrl && (
            <CardMedia
              sx={{ aspectRatio: '3/2', objectFit: 'cover' }}
              component="img"
              image={props.imageUrl}
              alt={props.imageAltText}
              loading="lazy"
            />
          )}
          {!props.imageUrl && <NewsCardImageDefault />}

          <CardContent
            sx={{
              backgroundColor: theme.palette.background.paper,
              height: 'fit-content',
              maxHeight: '94px',
            }}
          >
            <Stack height="100%">
              <Stack direction="row" justifyContent="space-between">
                <Box
                  sx={{
                    width: '100%',
                    maxWidth:
                      props.newIndicatorEnabled && isNew
                        ? `calc(100% - ${theme.spacing(8)})`
                        : '100%',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {props.title}
                  </Typography>
                </Box>

                {props.newIndicatorEnabled && isNew && (
                  <Box alignItems="start">
                    <Chip
                      label={tPortalLib.general.new()}
                      color="primary"
                      sx={{
                        fontSize: '12px',
                        height: '24px',
                        color: theme.palette.primary.contrastText,
                      }}
                    />
                  </Box>
                )}
              </Stack>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.text.secondary,
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  wordWrap: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineClamp: '2',
                  WebkitLineClamp: '2',
                }}
              >
                {props.description}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </ThemeProvider>
  );
};
